/**
 * Feature Flag Panel Component
 *
 * Admin interface for managing feature flags with toggle switches and
 * rollout percentage sliders. Updates Supabase and triggers audit logs.
 *
 * Features:
 * - List all feature flags with status indicators
 * - Toggle switch for each flag
 * - Slider for rollout percentage (0-100%)
 * - Save button to persist changes
 * - Shows current rollout percentage and user estimates
 * - Visual confirmation on save
 * - Loading states during updates
 * - Error handling with toast notifications
 */

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { Loader2, Save, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { useFeatureFlags, useFlagInvalidator } from '@/hooks/useFeatureFlags';
import { supabase } from '@/integrations/supabase/client';
import type { FeatureFlag } from '@/lib/featureFlags';

/**
 * Local state for a feature flag being edited
 */
interface FlagEditState {
  is_enabled: boolean;
  rollout_percentage: number;
}

/**
 * Props for FeatureFlagPanel component
 */
export interface FeatureFlagPanelProps {
  /** Callback when flags are successfully updated */
  onFlagsUpdated?: () => void;
  /** Estimated total user count for percentage calculations (optional) */
  totalUserCount?: number;
}

/**
 * Formats a number as locale string with K suffix for thousands
 */
function formatUserCount(count: number): string {
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K`;
  }
  return count.toString();
}

export function FeatureFlagPanel({ onFlagsUpdated, totalUserCount = 1000 }: FeatureFlagPanelProps) {
  const { flags, isLoading: flagsLoading, refetch } = useFeatureFlags({ staleTime: 0 });
  const { invalidateFlags } = useFlagInvalidator();
  const { toast } = useToast();

  const [flagsList, setFlagsList] = useState<FeatureFlag[]>([]);
  const [editedFlags, setEditedFlags] = useState<Record<string, FlagEditState>>({});
  const [isLoadingFlags, setIsLoadingFlags] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Fetch full flag details (not just enabled status)
  useEffect(() => {
    fetchFlags();
  }, []);

  const fetchFlags = async () => {
    setIsLoadingFlags(true);
    try {
      const { data, error } = await supabase
        .from('feature_flags')
        .select('*')
        .order('name');

      if (error) throw error;
      setFlagsList(data || []);

      // Initialize edited state with current values
      const initialEditState: Record<string, FlagEditState> = {};
      (data || []).forEach(flag => {
        initialEditState[flag.name] = {
          is_enabled: flag.is_enabled,
          rollout_percentage: flag.rollout_percentage,
        };
      });
      setEditedFlags(initialEditState);
    } catch (error) {
      console.error('Error fetching flags:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar as feature flags.',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingFlags(false);
    }
  };

  const handleToggleChange = (flagName: string, checked: boolean) => {
    setEditedFlags(prev => ({
      ...prev,
      [flagName]: {
        ...prev[flagName],
        is_enabled: checked,
      },
    }));
    setSaveSuccess(false);
  };

  const handlePercentageChange = (flagName: string, value: number[]) => {
    setEditedFlags(prev => ({
      ...prev,
      [flagName]: {
        ...prev[flagName],
        rollout_percentage: value[0],
      },
    }));
    setSaveSuccess(false);
  };

  const hasChanges = () => {
    return flagsList.some(flag => {
      const edited = editedFlags[flag.name];
      if (!edited) return false;
      return (
        edited.is_enabled !== flag.is_enabled ||
        edited.rollout_percentage !== flag.rollout_percentage
      );
    });
  };

  const handleSave = async () => {
    if (!hasChanges()) {
      toast({
        title: 'Nenhuma alteração',
        description: 'Não há mudanças para salvar.',
      });
      return;
    }

    setIsSaving(true);
    setSaveSuccess(false);

    try {
      // Update each changed flag
      const updates = flagsList
        .filter(flag => {
          const edited = editedFlags[flag.name];
          if (!edited) return false;
          return (
            edited.is_enabled !== flag.is_enabled ||
            edited.rollout_percentage !== flag.rollout_percentage
          );
        })
        .map(flag => ({
          name: flag.name,
          updates: {
            is_enabled: editedFlags[flag.name].is_enabled,
            rollout_percentage: editedFlags[flag.name].rollout_percentage,
          },
        }));

      for (const { name, updates } of updates) {
        const { error } = await supabase
          .from('feature_flags')
          .update(updates)
          .eq('name', name);

        if (error) throw error;
      }

      // Invalidate cache and refetch
      invalidateFlags();
      await refetch();
      await fetchFlags();

      setSaveSuccess(true);
      toast({
        title: 'Sucesso',
        description: `Feature flags atualizadas: ${updates.length} alterações salvas.`,
      });

      onFlagsUpdated?.();

      // Reset success indicator after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving flags:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível salvar as alterações.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    const resetState: Record<string, FlagEditState> = {};
    flagsList.forEach(flag => {
      resetState[flag.name] = {
        is_enabled: flag.is_enabled,
        rollout_percentage: flag.rollout_percentage,
      };
    });
    setEditedFlags(resetState);
    setSaveSuccess(false);
  };

  const getFlagVariant = (flagName: string): 'legacy' | 'refined' | null => {
    const flag = flagsList.find(f => f.name === flagName);
    if (!flag?.config) return null;
    return (flag.config as { variant?: string }).variant as 'legacy' | 'refined' || null;
  };

  if (isLoadingFlags) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <Card>
        <CardHeader>
          <CardTitle>Feature Flags</CardTitle>
          <CardDescription>
            Gerencie quais recursos estão ativos e para qual porcentagem de usuários
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Badge variant={flagsList.some(f => f.is_enabled) ? 'default' : 'secondary'}>
                {flagsList.filter(f => f.is_enabled).length} / {flagsList.length} ativas
              </Badge>
              {flagsList.length > 0 && (
                <span className="text-sm text-muted-foreground">
                  Estimativa de usuários: {formatUserCount(totalUserCount)}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              {hasChanges() && (
                <Button variant="outline" onClick={handleReset} disabled={isSaving}>
                  Descartar
                </Button>
              )}
              <Button
                onClick={handleSave}
                disabled={!hasChanges() || isSaving}
                className={saveSuccess ? 'bg-green-600 hover:bg-green-700' : undefined}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : saveSuccess ? (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Salvo
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Salvar Alterações
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rollback Alert */}
      {flagsList.some(f => f.name === 'color_refined' && f.is_enabled) && (
        <Alert className="border-blue-200 bg-blue-50">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            <strong>Rollback rápido:</strong> Para reverter o tema refinado, desative a flag &quot;color_refined&quot;.
            Todos os usuários verão o tema legado imediatamente.
          </AlertDescription>
        </Alert>
      )}

      {/* Flags List */}
      <div className="space-y-4">
        {flagsList.map(flag => {
          const edited = editedFlags[flag.name];
          const isChanged = edited && (
            edited.is_enabled !== flag.is_enabled ||
            edited.rollout_percentage !== flag.rollout_percentage
          );

          const estimatedUsers = Math.round((edited?.rollout_percentage || flag.rollout_percentage) / 100 * totalUserCount);

          return (
            <Card key={flag.name} className={isChanged ? 'border-blue-300' : undefined}>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  {/* Header with name, description, and toggle */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-foreground">{flag.name}</h3>
                        {flag.is_enabled ? (
                          <Badge variant="default" className="text-xs">Ativa</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs">Inativa</Badge>
                        )}
                        {isChanged && (
                          <Badge variant="outline" className="text-xs border-blue-300 text-blue-600">
                            Modificada
                          </Badge>
                        )}
                        {getFlagVariant(flag.name) && (
                          <Badge variant="secondary" className="text-xs">
                            {getFlagVariant(flag.name)}
                          </Badge>
                        )}
                      </div>
                      {flag.description && (
                        <p className="text-sm text-muted-foreground">{flag.description}</p>
                      )}
                    </div>
                    <Switch
                      checked={edited?.is_enabled ?? flag.is_enabled}
                      onCheckedChange={(checked) => handleToggleChange(flag.name, checked)}
                      disabled={isSaving}
                    />
                  </div>

                  {/* Rollout Percentage Slider */}
                  <div className="space-y-3 pl-1">
                    <div className="flex items-center justify-between">
                      <Label htmlFor={`percentage-${flag.name}`} className="text-sm">
                        Porcentagem de Rollout
                      </Label>
                      <span className="text-sm font-medium">
                        {edited?.rollout_percentage ?? flag.rollout_percentage}%
                        {totalUserCount > 0 && (
                          <span className="text-muted-foreground ml-1">
                            (~{formatUserCount(estimatedUsers)} usuários)
                          </span>
                        )}
                      </span>
                    </div>
                    <Slider
                      id={`percentage-${flag.name}`}
                      value={[edited?.rollout_percentage ?? flag.rollout_percentage]}
                      onValueChange={(value) => handlePercentageChange(flag.name, value)}
                      min={0}
                      max={100}
                      step={5}
                      disabled={isSaving || (edited?.is_enabled ?? flag.is_enabled)}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>0%</span>
                      <span>25%</span>
                      <span>50%</span>
                      <span>75%</span>
                      <span>100%</span>
                    </div>
                    {(edited?.is_enabled ?? flag.is_enabled) && (
                      <Alert>
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription className="text-xs">
                          Quando a flag está ativa, o rollout é de 100%. Ajuste a porcentagem para
                          rollout gradual quando a flag estiver inativa.
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>

                  {/* Current vs New Values (if changed) */}
                  {isChanged && (
                    <div className="pt-2 border-t text-xs text-muted-foreground">
                      <div className="flex justify-between">
                        <span>
                          Antes: {flag.is_enabled ? 'Ativa' : 'Inativa'} ({flag.rollout_percentage}%)
                        </span>
                        <span className="text-blue-600 font-medium">
                          Depois: {edited?.is_enabled ? 'Ativa' : 'Inativa'} ({edited?.rollout_percentage}%)
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}

        {flagsList.length === 0 && (
          <Card>
            <CardContent className="pt-6 text-center py-12">
              <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">Nenhuma feature flag encontrada</h3>
              <p className="text-sm text-muted-foreground">
                Não há feature flags configuradas no sistema.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export default FeatureFlagPanel;
